"use client";
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Edit, Trash2, Eye } from "lucide-react";
import { formatDate } from "@/lib/utils";
import { Staff, Appointment, StaffApiResponse } from "@/types";
import { userService } from "@/services/userService";
import { ca } from "date-fns/locale";

interface StaffTableProps {
  staff: Staff[];
  appointments: Appointment[];
  getStaffStats: (staffId: string) => {
    total: number;
    completed: number;
    upcoming: number;
  };
  onView: (member: Staff) => void;
  onEdit: (member: Staff) => void;
  onDelete: (member: Staff) => void;
}

interface SortConfig {
  key: keyof StaffApiResponse;
  direction: "asc" | "desc";
}

export function StaffTable({
  staff,
  appointments,
  getStaffStats,
  onView,
  onEdit,
  onDelete,
}: StaffTableProps) {
  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold">
            Staff Members ({staff.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="border-b hover:bg-transparent">
                <TableHead>ID</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Joined Date</TableHead>
                <TableHead>Total Jobs</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {staff.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={9}
                    className="text-center text-muted-foreground"
                  >
                    No staff members found
                  </TableCell>
                </TableRow>
              ) : (
                staff.map((member) => {
                  const stats = getStaffStats(member.id);

                  return (
                    <TableRow key={member.id}>
                      <TableCell className="font-medium">
                        {member.id.split("-")[0]}
                      </TableCell>
                      <TableCell>{member.name}</TableCell>
                      <TableCell>{member.phone}</TableCell>
                      <TableCell>{member.email || "-"}</TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            member.status === "active" ? "outline" : "secondary"
                          }
                          className={
                            member.status === "active" ? "bg-green-50 text-green-700 border-green-100" : "bg-red-50 text-red-700 border-red-100"
                          }
                        >
                          {member.status === "active" ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell>{formatDate(member.joinedDate)}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{member.totalJobs}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => onView(member)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => onEdit(member)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => onDelete(member)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </>
  );
}
